<template>
  <div class="demo-dashboard">
    <BMap @loaded="bmapLoaded" />
    <div class="left">
      <BorderWrap v-for="(chart, index) in leftCharts" v-bind="chart" :key="index" />
    </div>
    <div class="right" v-if="hasDetection">
      <BorderWrap v-for="(chart, index) in rightCharts" v-bind="chart" :key="index" />
    </div>
    <div class="middle">
      <div class="tabs-wrap">
        <TabWrap :label="$t('deviceCount')" icon="icon-string" :value="deviceTotal" />
        <TabWrap
          :label="$t('alarmCount')"
          :unit="$t('times')"
          icon="icon-headset"
          :value="alarmCount"
        />
        <TabWrap
          v-if="hasDetection"
          :label="$t('checkCount')"
          :unit="$t('times')"
          icon="icon-detection"
          :value="detectionCount"
        />
      </div>

      <BorderWrap size="large" v-if="hasDetection">
        <div class="h-table-wrap">
          <div class="thead">
            <div class="column" v-for="item in table.thead" :key="item.prop">
              {{ $t(item.label) }}
            </div>
          </div>
          <div class="tbody">
            <div class="row" v-for="(row, index) in table.data" :key="`row-${index}`">
              <div class="column" v-for="(item, i) in table.thead" :key="`r${index}col-${i}`">
                {{ getData(row, item) }}
              </div>
            </div>
          </div>
        </div>
      </BorderWrap>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import Type from '@/store/mutation-type';
import BMap from '@/components/bmapGL';
import bmapTheme from '@/utils/bmapTheme';
import BorderWrap from './components/border-wrap';
import TabWrap from './components/tab-wrap';

import { makeDoughnut, makeLine, makeBar, setData } from './utils';
import {
  getAlarmCount,
  getAlarmList,
  getYearAlarm,
  getYearAlarmTrend,
  getDetectionCount,
  getDetectionList,
  makeTableData,
  getYearDetection,
  getYearDetectionTrend
} from './utils/mock/index';
import { getTime } from '@/utils/date';
import { timer } from '@/utils/utils';
import { infoContent, makeDeviceMarker, patchDeviceMarker } from '@/utils/bmapUtil';
import { isString } from '@/utils/is';

// import {
//   makeView,
//   makeHeatGridLayer,
//   toPointGeoJson,
//   to,
//   toCoordinate,
//   toBmapCoordinate
// } from '@/utils/bmapUtil';

export default {
  components: { BMap, BorderWrap, TabWrap },
  data() {
    return {
      bmap: null,
      interval: null,
      alarmCount: 0,
      dayAlarm: makeDoughnut(),
      yearAlarm: makeBar({ xLabelRotate: 20 }),
      yearAlarmTrend: makeLine(),

      detectionCount: 0,
      dayDetection: makeDoughnut(),
      yearDetection: makeBar({ xLabelRotate: 20 }),
      yearDetectionTrend: makeLine(),
      markerMap: {},
      infoWindow: null,
      timer: null,
      table: {
        thead: [
          { prop: 'checkTime', label: 'checkTime' },
          { prop: 'sn', label: 'deviceNum' },
          { prop: 'dept', label: 'organization' },
          { prop: 'result', label: 'checkResult' }
        ],
        data: []
      }
    };
  },
  computed: {
    ...mapState('socket', ['devicesSourseMap']),
    ...mapState('admin', ['userInfo']),
    deviceTotal() {
      return this.devicesSourseMap.length;
    },
    leftCharts() {
      const year = new Date().getUTCFullYear();
      return [
        { title: this.$t('dayAlarmCount'), options: this.dayAlarm },
        { title: `${year} ${this.$t('yearAlarmCount')}`, options: this.yearAlarm },
        { title: `${year} ${this.$t('yearAlarmTrend')}`, options: this.yearAlarmTrend }
      ];
    },
    rightCharts() {
      const year = new Date().getUTCFullYear();
      return [
        { title: this.$t('dayCheckCount'), options: this.dayDetection },
        { title: `${year} ${this.$t('yearCheckCount')}`, options: this.yearDetection },
        {
          title: `${year} ${this.$t('yearCheckTrend')}`,
          options: this.yearDetectionTrend
        }
      ];
    },
    hasDetection() {
      return this.userInfo.displayType === 2;
    }
  },
  methods: {
    ...mapMutations('app', {
      enableAlarmNotice: Type.ENABLE_ALARM_NOTICE
    }),
    getData(row, item) {
      const v = row[item.prop];
      if (isString(v) && v.slice(0, 1) === '_') {
        return this.$t('demo.' + v.slice(1));
      } else {
        return v;
      }
    },
    bmapLoaded(bmap) {
      this.bmap = bmap;
      this.$nextTick(() => {
        const point = new BMapGL.Point(106.387456, 34.925406);
        this.bmap.centerAndZoom(point, 5);
        this.bmap.setMapStyleV2({ styleJson: bmapTheme['dark'] });
        this.initMarker();
        this.intervalUpdate();
      });
    },
    initChart() {
      const alarmList = getAlarmList();
      this.alarmCount = getAlarmCount(alarmList);
      setData(this.dayAlarm, alarmList);
      setData(this.yearAlarm, getYearAlarm());
      setData(this.yearAlarmTrend, getYearAlarmTrend());

      const detectionList = getDetectionList();
      this.detectionCount = getDetectionCount(detectionList);
      setData(this.dayDetection, detectionList);
      setData(this.yearDetection, getYearDetection());
      setData(this.yearDetectionTrend, getYearDetectionTrend());
    },
    initTable() {
      const data = makeTableData(5, {
        begin: Date.now() - getTime('m') * 3.5,
        timeAutoIncrement: getTime('s') * 40
      });

      this.table.data.splice(0, Infinity, ...data);

      this.interval = setInterval(() => {
        const data = makeTableData(1, { begin: Date.now(), timeAutoIncrement: 1 });
        this.table.data.push(...data);
        this.scrollToBottom();
      }, getTime('s') * 40);
    },
    scrollToBottom() {
      const el = document.querySelector('.demo-dashboard .tbody');
      const toBottom = () => {
        el.scrollTo({
          top: el.scrollHeight - el.clientHeight,
          left: 0,
          behavior: 'smooth'
        });
      };
      toBottom();
      if (this.table.data.length > 20) {
        timer(() => {
          this.table.data.splice(0, this.table.data.length - 10);
          toBottom();
        }, 1000);
      }
    },
    initMarker() {
      const devices = this.devicesSourseMap;
      const map = this.markerMap;
      let marker;
      devices.forEach(e => {
        if (!e.longitude || !e.latitude) return;
        marker = map[e.sn] = makeDeviceMarker({
          state: e.state,
          coordinate: [e.longitude, e.latitude]
        });
        this.bmap.addOverlay(marker);
        marker.addEventListener('click', () => {
          this.showInfoWindow(e);
        });
      });
    },
    patchMarker(device) {
      const marker = this.markerMap[device.sn];
      patchDeviceMarker({
        marker,
        state: device.state,
        coordinate: [device.longitude, device.latitude]
      });
    },
    intervalUpdate() {
      this.timer = setInterval(() => {
        const devices = this.devicesSourseMap;
        devices.forEach(e => {
          this.patchMarker(e);
        });
      }, 3000);
    },
    showInfoWindow({ name, state = -1, sn, longitude, latitude, deviceType }) {
      if (this.infoWindow) {
        this.infoWindow.setContent(infoContent(name, state, sn, deviceType, this.$router));
        this.infoWindow.setConfig({ title: deviceType });
      } else {
        this.infoWindow = new BMapGL.InfoWindow(
          infoContent(name, state, sn, deviceType, this.$router),
          { offset: new BMapGL.Size(0, -20), title: deviceType }
        );
      }
      this.bmap.openInfoWindow(this.infoWindow, new BMapGL.Point(longitude, latitude));
    }
  },
  created() {
    this.initChart();
    this.initTable();
    this.enableAlarmNotice(false);
  },
  beforeDestroy() {
    clearInterval(this.interval);
    clearInterval(this.timer);
    this.enableAlarmNotice(true);
  }
};
</script>

<style lang="scss">
.demo-dashboard {
  width: 100%;
  height: 100%;
  position: relative;

  & > .left {
    position: absolute;
    left: 15px;
    top: 15px;
    z-index: 10;

    pointer-events: none;

    width: 24%;
    height: 96%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .border-wrap {
      height: 30%;

      pointer-events: all;
    }
  }

  & > .right {
    position: absolute;
    right: 15px;
    top: 15px;
    z-index: 10;

    pointer-events: none;

    width: 24%;
    height: 96%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .border-wrap {
      height: 30%;

      pointer-events: all;
    }
  }

  .middle {
    position: absolute;
    left: calc(24% + 30px);
    top: 15px;
    z-index: 10;

    width: calc(52% - 60px);
    height: 96%;

    display: flex;
    flex-direction: column;
    justify-content: space-between;
    pointer-events: none;

    .border-wrap {
      width: 100%;
      height: 30%;
    }

    .h-table-wrap {
      height: 100%;
      font-size: 12px;
      pointer-events: all;

      .thead {
        display: flex;
        justify-content: space-between;

        color: #42b6c5;
        padding: 14px 0;
      }

      .tbody {
        height: calc(100% - 50px);
        overflow-y: scroll;
        margin-right: -18px;

        .row {
          padding: 8px 0;

          display: flex;
          justify-content: space-between;
        }
      }

      .column {
        flex: 1;
        text-align: center;
      }
      .column:nth-of-type(2) {
        width: 15%;
      }
      .column:nth-of-type(3) {
        width: 25%;
      }
    }
    .tabs-wrap {
      display: flex;
      flex-direction: column;

      .h-tab-wrap {
        margin-bottom: 10px;
      }
    }
  }

  .custom-info-content {
    color: #aaa;
    > div {
      line-height: 26px;
    }
    .el-button {
      float: right;
    }
    .state-text {
      @include state-textstyle;
    }
  }
}
</style>
